<template>
  <div class="slaes-table table-component">
    <artisio-table :pagination="tableData.pagination"
                   :sort="tableData.ordering"
                   :items="tableData.rows"
                   :busy="tableData.loading"
                   :fields="tableFields"
                   :checkboxes="true"
                   :search="tableData.search"
                   :enable-simple-search="false"
                   show-toggle-columns
                   export-vuex-module-name="sales"
                   export-permission-name="export_auction"
                   :export-attributes="computedExportAttributes"
                   :extra-data="toggleColumns"
                   @paramsChanged="getTableData"
                   @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns"
                   :enable-frontend-export="enableFrontendExport"
                   filter-toggle-string="sales-table">

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="sales-table">
          <sale-search/>
        </search-collapse>
      </template>

      <template slot="cell(sale_no)" slot-scope="{item: sale}">
        {{ sale.sale_no }}
        <action-buttons
          :view-permission="'view_auction'"
          :edit-permission="'change_auction'"
          :delete-permission="'delete_auction'"
          @viewClick="onViewClick(sale)"
          @editClick="onEditClick(sale)"
          @deleteClick="onRemoveClick(sale)"/>
      </template>

      <template slot="cell(title)" slot-scope="{item}">
        {{ item.title | masterLanguage | striptags | truncate(50) }}
      </template>

      <template slot="cell(currency)" slot-scope="{item}">
        {{ item.currency.code }}
      </template>

      <template slot="cell(items)" slot-scope="{item: sale}">
        <router-link :to="{name: 'sales.lots',params: {id: sale.uuid}}">
          <b-badge variant="primary" class="hover-cursor">
            {{ $t('{item_count} items', {item_count: sale.total_items}) }}
          </b-badge>
        </router-link>
      </template>

      <template slot="cell(department)" slot-scope="{item: sale}">
        <template v-if="sale.department">
          <router-link v-if="$hasPermission('setup')"
                       :to="{name: 'departments', params: {department_uuid: sale.department_uuid}}">
            {{ sale.department.name | masterLanguage }}
          </router-link>
          <span v-else>{{ sale.department.name | masterLanguage }}</span>
        </template>
      </template>

      <template slot="cell(branch_uuid)" slot-scope="{item: sale}">
        <template v-if="sale.branch">
          <router-link :to="{name: 'branches', params: {branch_uuid: sale.branch_uuid}}">
            {{ sale.branch.name }}
          </router-link>
        </template>
      </template>

      <template slot="cell(updated_at)" slot-scope="{item: sale}">
        {{ sale.updated_at | toDate }}
      </template>

      <template slot="cell(start_date)" slot-scope="{item: sale}">
        <template v-if="!enableTimedSessions || sale.type === 'live'">
          {{ sale.start_date | toDateTime }} <span v-if="sale.end_date"> - {{ sale.end_date | toDateTime }}</span>
        </template>
        <template v-else>
          <span v-for="(date) in sale.dates" :key="`sale-table-date-cell-${date.uuid}`">
            {{ date.start_date | toDateTime }} - {{ date.end_date | toDateTime }} <br>
          </span>
        </template>
      </template>

      <template slot="cell(live_reference)" slot-scope="{item: sale}">
        <a :href="sale.live_url" target="_blank">{{ sale.live_reference }}</a>
      </template>

      <template slot="cell(status)" slot-scope="{item: sale}">
        <span v-as-status="sale.status">
          {{ sale.status | toUpperCaseFirst | removeUnderscores | translate }}
        </span>
      </template>

      <template slot="cell(type)" slot-scope="{item: sale}">
        {{ sale.type | toUpperCaseFirst | translate }}
      </template>

      <template slot="cell(created_data)" slot-scope="{item: sale}">
        {{ sale.created_at | toDate }} by {{ sale.created_by.display_name }}
      </template>

      <template slot="cell(updated_data)" slot-scope="{item: sale}">
        {{ sale.updated_at | toDate }} by {{ sale.updated_by.display_name }}
      </template>

      <template slot="cell(premiums)" slot-scope="{item: sale}">
        <template v-for="premium in sale.premiums">
          {{ premium.percent | asPercentage }} - {{ premium.amount_over | asCurrency(currencySymbol(sale)) }}
          <br/>
        </template>
      </template>

      <template slot="cell(estimate)" slot-scope="{item: sale}">
        {{ sale.low_estimate_amount | asCurrency(currencySymbol(sale)) }} -
        {{ sale.high_estimate_amount | asCurrency(currencySymbol(sale)) }}
      </template>

      <template slot="cell(reserve)" slot-scope="{item: sale}">
        {{ sale.reserve_amount | asCurrency(currencySymbol(sale)) }}
      </template>


      <template slot="cell(after_sale_end_date)" slot-scope="{item: sale}">
        <span v-if="sale.after_sale_end_date">
          {{ sale.after_sale_end_date | toDateTime }}
        </span>
      </template>

      <template slot="cell(require_credit_card_verification)" slot-scope="{item}">
        <span class="badge bg-success text-white" v-if="item.require_credit_card_verification">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>

      <template slot="cell(require_identity_verification)" slot-scope="{item}">
        <span class="badge bg-success text-white" v-if="item.require_identity_verification">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>

      <template slot="cell(account_managers)" slot-scope="{item}">
        <div v-if="item.account_managers && item.account_managers.length > 0" class="text-wrap">
          <span v-for="(am, index) in item.account_managers" :key="`sale-table-am-${am.uuid}`">
            {{ am.display_name }}<span v-if="index < item.account_managers.length - 1">,</span>
          </span>
        </div>
      </template>

      <template slot="cell(is_private)" slot-scope="{item}">
        <span class="badge bg-secondary" v-if="item.is_private">{{ $t('Private') }}</span>
        <span class="badge bg-success text-white" v-else>{{ $t('Public') }}</span>
      </template>

      <template slot="cell(auctioneers_sheet)" slot-scope="{item}">
        <div>{{ item.auctioneers_sheet_status }}</div>
        <div v-if="item.auctioneers_sheet_status == 'Locked'">
          <a href="javascript:void(0)" @click="viewAuctioneersSheet(item.uuid)">
            {{ item.auctioneers_sheet_no | translate}}
          </a>
        </div>
      </template>

      <template slot="cell(after_sale_auctioneers_sheet)" slot-scope="{item}">
        <div>{{ item.after_sale_auctioneers_sheet_status }}</div>
        <div v-if="item.after_sale_auctioneers_sheet_status == 'Locked'">
          <a href="javascript:void(0)"
             @click="viewAfterSaleAuctioneersSheet(item.uuid)">{{ item.after_sale_auctioneers_sheet_no }}</a>
        </div>
      </template>

      <template slot="cell(buyer_group_account)" slot-scope="{item: sale}" v-if="vendorBuyerGroupAccountsEnabled">
        {{ sale.buyer_group_account }}
      </template>

      <template slot="cell(vendor_group_account)" slot-scope="{item: sale}" v-if="vendorBuyerGroupAccountsEnabled">
        {{ sale.vendor_group_account }}
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <div
          v-html="$options.filters.asDynamicField(
            $options.filters.masterLanguage(item.dynamic_fields)[dynamicField],
            field.type,
            field.choices
          )">
        </div>
      </template>

    </artisio-table>
  </div>
</template>

<script>
  import i18nHelper from "./../../core/services/i18n.service";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {SOURCE_SALES_TABLE} from "../constants";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import SaleSearch from "./SaleSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import i18n from "../../shared/i18n";
  import _ from 'lodash';
  import SalesService from "@/modules/sales/SalesService";

  const {mapState, mapActions, mapGetters} = createNamespacedHelpers('sales');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "SalesTable",
    components: {SearchCollapse, SaleSearch, ActionButtons, ArtisioTable},
    props: {
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    data: () => {
      return {
        masterLanguage: i18nHelper.getMasterLanguage(),
      }
    },
    computed: {
      ...globalMapState(['enableTimedSessions', 'vendorBuyerGroupAccountsEnabled']),
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        // loading: state => state.tableData.loading,
        // rows: state => state.tableData.rows,
        // pagination: state => state.tableData.pagination,
        // ordering: state => state.tableData.ordering,
        // search: state => state.tableData.search,
        // toggleColumns: state => state.tableData.toggleColumns,
      }),
      ...mapGetters({
        tableFields: 'tableFields',
        toggleColumns: 'toggleColumns',
      }),
      computedExportAttributes() {
        const masterLanguage = i18nHelper.getMasterLanguage()
        const titleKey = `title.${masterLanguage.key}`
        const deptKey = `department.name.${masterLanguage.key}`
        return {
          sale_no: i18n.t('Sale'),
          [titleKey]: i18n.t('Title'),
          total_items: i18n.t('Items'),
          [deptKey]: i18n.t('Department'),
          'currency.code': i18n.t('Currency'),
          start_date: i18n.t('Start Date'),
          live_reference: i18n.t('Online sale ref. no.')
        }
      },
      tableFields() {
        let clonedColumns = _.cloneDeep(this.toggleColumns.fields);
        if (!this.vendorBuyerGroupAccountsEnabled) {
          this.toggleColumns.excludeColumns = ['vendor_group_account', 'buyer_group_account'];
          clonedColumns = clonedColumns.filter(f => !this.toggleColumns.excludeColumns.includes(f.key));
        }
        return clonedColumns.filter(f => f.isVisible);
      },
      // extraData() {
      //   return {
      //     ...this.toggleColumns,
      //     excludeColumns: [],
      //     tableName: SALES_TABLE_NAME,
      //   }
      // }
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'removeSale', 'updateToggleColumns', 'getModalObject']),
      async onViewClick(data) {
        this.$router.push({
          name: 'sales.view',
          params: {id: data.uuid}
        });
      },
      async onEditClick(item) {
        this.showModal()
        this.getModalObject({uuid: item.uuid, source: SOURCE_SALES_TABLE})
      },
      async onRemoveClick(sale) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete sale {sale_no}?", {sale_no: sale.sale_no}),
          this.$i18n.t('Sale {sale_no} was successfully deleted', {sale_no: sale.sale_no}),
          this.removeSale,
          sale,
        );
      },
      currencySymbol(sale) {
        if (sale && sale.currency) {
          return sale.currency.symbol;
        }
        return null;
      },
      viewAuctioneersSheet(uuid) {
        SalesService.generateOrRetrieveAuctioneersSheet(this, uuid, false, true);
      },
      viewAfterSaleAuctioneersSheet(uuid) {
        SalesService.generateOrRetrieveAfterSaleAuctioneersSheet(this, uuid, false, true);
      }
    },
    mounted() {
      this.getTableData();
    },
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
      .col-saleno {
        min-width: 160px;
      }

      .status-col {
        min-width: 90px;
      }

      .start-date-col {
        min-width: 150px;
      }

      .title-col {
        min-width: 150px;
      }

      .department-col {
        min-width: 140px;
      }

      .currency-col {
        min-width: 95px;
      }

      .items-col {
        min-width: 80px;
      }

      .type-col {
        min-width: 100px;
      }

      .live-reference-col {
        min-width: 140px;
      }

      .premiums-col {
        min-width: 100px;
      }

      .payment-terms-col {
        min-width: 100px;
      }

      .settlement-days-col {
        min-width: 100px;
      }

      .reserved-items-col {
        min-width: 100px;
      }

      .total-lots-col {
        min-width: 100px;
      }

      .estimate-col {
        min-width: 100px;
      }

      .reserve-col {
        min-width: 100px;
      }

      .sold-lots-col {
        min-width: 100px;
      }

      .unsold-lots-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .account_managers-col {
        min-width: 100px;
        max-width: 300px;
      }
    }
  }
</style>

